import React, { lazy, Suspense, useEffect, useId, useState } from "react";
import type { MemoryTab } from "../types/memory";

const AnniversaryLetter = lazy(
  () => import("../features/anniversary/AnniversaryLetter")
);
const BirthdayPage = lazy(() => import("../features/birthday/BirthdayPage"));

const tabFallback = (
  <div className="flex min-h-[50vh] items-center justify-center text-gray-500">
    불러오는 중…
  </div>
);

const focusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2";

interface MemoryTabsPageProps {
  tab: MemoryTab;
  onTabChange: (tab: MemoryTab) => void;
  onBack: () => void;
}

const navItems: { id: MemoryTab; label: string; description: string }[] = [
  { id: "anniversary", label: "2주년 편지", description: "함께한 이야기" },
  {
    id: "birthday",
    label: "소윤이 생일",
    description: "2026년 4월 24일",
  },
];

const MemoryTabsPage: React.FC<MemoryTabsPageProps> = ({
  tab,
  onTabChange,
  onBack,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const drawerId = useId();
  const titleId = useId();

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const currentLabel =
    tab === "anniversary" ? "2주년" : "소윤이 생일";

  const selectTab = (next: MemoryTab) => {
    onTabChange(next);
    setMenuOpen(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="sticky top-0 z-30 border-b border-gray-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center gap-2 px-3 py-3 sm:px-4">
          <button
            type="button"
            onClick={onBack}
            className={`shrink-0 rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100 ${focusRing}`}
            aria-label="메인으로"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="min-w-0 flex-1 text-center">
            <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
              지금 보기
            </p>
            <p id={titleId} className="truncate text-base font-semibold text-gray-900">
              {currentLabel}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className={`shrink-0 rounded-full p-2 text-gray-700 transition-colors hover:bg-gray-100 ${focusRing}`}
            aria-expanded={menuOpen}
            aria-controls={drawerId}
            aria-haspopup="true"
            aria-label={menuOpen ? "메뉴 닫기" : "페이지 메뉴 열기"}
          >
            {menuOpen ? (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40"
          aria-label="메뉴 닫기"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside
        id={drawerId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${drawerId}-heading`}
        aria-hidden={!menuOpen}
        className={`fixed inset-y-0 right-0 z-50 flex w-[min(100%,18rem)] flex-col border-l border-gray-200 bg-white shadow-2xl transition-transform duration-300 ease-out sm:w-80 ${
          menuOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 id={`${drawerId}-heading`} className="text-sm font-semibold text-gray-900">
            어디로 갈까?
          </h2>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className={`rounded-full p-2 text-gray-500 hover:bg-gray-100 ${focusRing}`}
            aria-label="닫기"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3" aria-label="추억 페이지">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const active = tab === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => selectTab(item.id)}
                    className={`flex w-full flex-col rounded-2xl px-4 py-3 text-left transition-colors ${focusRing} ${
                      active
                        ? "bg-rose-50 text-rose-900 ring-1 ring-rose-100"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="font-medium">{item.label}</span>
                    <span className="text-xs text-gray-500">{item.description}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-6 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onBack();
              }}
              className={`flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 ${focusRing}`}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              메인 화면으로
            </button>
          </div>
        </nav>
      </aside>

      <main className="min-h-0 flex-1" aria-labelledby={titleId}>
        <Suspense fallback={tabFallback}>
          {tab === "anniversary" && (
            <div key="anniversary">
              <AnniversaryLetter />
            </div>
          )}
          {tab === "birthday" && (
            <div key="birthday">
              <BirthdayPage />
            </div>
          )}
        </Suspense>
      </main>
    </div>
  );
};

export default MemoryTabsPage;
export type { MemoryTab } from "../types/memory";
